import { useNavigate, useParams } from "react-router-dom";
import { Container } from "reactstrap";

import AppPageHeader from "../../../components/Headers/AppPageHeader";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { Prefill, Layout } from "../../../types/GlobalInterfaces";
import { AppConfirm } from "../../../utils/PopupUtil";
import Settings from "./GlobalComponents/CloudSettings";
import ManageTags from "../../../components/Tags/ManageTags";

// Layouts
import useUpdateGlobals from "../../../api/api-hooks/globals/useUpdateGlobal";
import useDeleteGlobals from "../../../api/api-hooks/globals/useDeleteGlobal";
import useCreateGlobals from "../../../api/api-hooks/globals/useCreateGlobal";
import LayoutsList from "./GlobalComponents/LayoutList";
import QuickCreatesList from "./GlobalComponents/QuickCreatesList";
import AppHelmet from "../../../components/AppComponents/AppHelmet";
import RelatedRecords from "../RelatedRecords/RelatedRecords";
import AIModels from "../AIModels/AIModels";

const tabs = [
  {
    title: "Settings",
    activeValue: "Settings",
  },
  {
    title: "Defaults",
    activeValue: "Defaults",
  },
  {
    title: "Layouts",
    activeValue: "Layouts",
  },
  {
    title: "Quick Creates",
    activeValue: "QuickCreates",
  },
  {
    title: "Tags",
    activeValue: "Tags",
  },
  {
    title: "AI Models",
    activeValue: "AIModels",
  },
];

export type GlobalsTypes = "QuickCreates" | "Layouts";

export type CloudSettingTabs = GlobalsTypes | "Settings" | "Defaults" | "Tags";

export interface ListProps {
  createGlobal: any;
  isCreating: boolean;
  updateGlobal: any;
  updateID: string | null | undefined;
  deleteGlobal: any;
  deleteID: string | null | undefined;
}

/**
 * Top Level View for managing Global Items in a cloud
 * Currently supports Global Quick Creates, and Global Layouts
 *
 * Contains the Header, Navbar by default
 * Renders Global Item Lists based on current tab.
 */
const Globals = () => {
  const { tenantID, tab } = useParams();
  const navigate = useNavigate();
  const { updateGlobal, updateID } = useUpdateGlobals();
  const { deleteGlobal, deleteID } = useDeleteGlobals();
  const { createGlobal, createGlobalLoading } = useCreateGlobals();

  const handleTabChange = (value: GlobalsTypes) => {
    navigate(`/clouds/${tenantID}/settings/globals/${value}`);
  };

  return (
    <>
      <AppHelmet title="Globals Manager - GrowthCloud.com" />
      <Container fluid>
        <div className="header">
          <div className="header-body">
            <AppPageHeader
              borderless={true}
              title="Settings"
              description="View & manage all core cloud features, edit cloud name"
            />
          </div>
        </div>
        <div className="mt--4">
          <AppNavbar tabs={tabs} activeTab={tab} onSelect={handleTabChange} />
          {tab === "Settings" && <Settings tenantID={tenantID!} />}
          {tab === "Layouts" && (
            <LayoutsList
              createGlobal={handleCreate}
              isCreating={createGlobalLoading}
              updateGlobal={handleUpdate}
              updateID={updateID}
              deleteGlobal={handleDelete}
              deleteID={deleteID}
            />
          )}
          {tab === "QuickCreates" && (
            <QuickCreatesList
              createGlobal={handleCreate}
              isCreating={createGlobalLoading}
              updateGlobal={handleUpdate}
              updateID={updateID}
              deleteGlobal={handleDelete}
              deleteID={deleteID}
            />
          )}
          {tab === "Defaults" && <RelatedRecords />}
          {tab === "Tags" && (
            <div className="py-3">
              <ManageTags
                title="Cloud Tags"
                scope={{ Type: "Cloud" }}
                headerSize="sm"
                description="Create and manage tags available across your cloud. Cloud tags can be applied to records across all modules."
              />
            </div>
          )}
          {tab === "AIModels" && <AIModels />}
        </div>
      </Container>
    </>
  );

  // Create a Global Item
  function handleCreate(payload: {
    key: string;
    onSuccess: () => void;
    payload: Prefill | Layout;
  }) {
    const newGlobal = payload.payload;
    createGlobal({
      global: newGlobal,
      globalsType: tab as GlobalsTypes,
      onSuccess: payload.onSuccess,
    });
  }

  // Update a Global Item
  function handleUpdate(global: {
    id: string;
    key: string;
    onSuccess: () => void;
    payload: Prefill | Layout;
  }) {
    const updateItem = global.payload;
    updateItem.id = global.id;

    updateGlobal({
      updateItem,
      globalsType: tab as GlobalsTypes,
      onSuccess: () => {
        global.onSuccess ? global.onSuccess() : null;
      },
    });
  }

  // Delete a global item
  async function handleDelete(globalID: string, moduleAPIName: GlobalsTypes) {
    const result = await AppConfirm({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete this Global Quick Create? All instances of this quick create will be deleted across the cloud.",
    });

    if (!result.isConfirmed) return;

    deleteGlobal({ globalID, globalsType: moduleAPIName });
  }
};

export default Globals;
